import { useEffect, useMemo, useRef, useState } from 'react'
import { TrackEvent, useTracking } from '../common/tracking'
import { useParams } from 'react-router-dom'
import { useAppSelector } from '../../../../store/hooks'
import { selectAuthenticatedUser } from '../../../../store/auth/authSlice'
import { selectRoomSessionStartedAt } from '../../../../store/meetingSummary/roomSession'
import { CopyData } from './copy'
import {
  CONSENT_TRACKING_PRESET_ID,
  ConsentResult,
  getUserMetadataConsent,
  mapToUserConsent,
  UserConsent,
  UserConsentState,
} from '../../../../components/Consent/model'
import { AIFeatureInfo, useGetManualParticipantsQuery, useGetRoomSessionQuery, useListRoomEventsQuery } from '../../../../store/media/media'
import { ASSIGNMENT_ROLE_NAME_MAPPING } from './ConfigureAssignment/AssignmentRoleNameMapping'
import { parseMetadata } from './utils'
import { DEFAULT_ROLE } from './hooks/users'
import { User } from '../../../../models/User'
import { StructuredSummaryResult } from '../../../../models/AiResult'
import { useGetAiResultsQuery } from '../../../../store/ai/ai'
import { selectGroupedTasks } from '../../../../store/task/task'
import { useAccount } from '../../../../common/useAccount'
import { type ManualParticipant } from '../../../../models/ManualParticipant'
import { didManualParticipantAttend } from './ConfigureAssignment/utils'

export const useTrackRoomSessionOpened = () => {
  const { trackingClient } = useTracking()
  const { roomId = '', sessionId = '' } = useParams()

  const user = useAppSelector(selectAuthenticatedUser)

  useEffect(() => {
    if (roomId && sessionId && !user?.isGhosted) {
      trackingClient.track({
        eventName: TrackEvent.ROOM_SESSION_OPENED,
        eventDetails: {
          roomId,
          sessionId,
        },
      })
    }
  }, [user, roomId, sessionId, trackingClient])
}

export const useCopyData = (): CopyData => {
  const roomSessionStartedAt = useAppSelector(selectRoomSessionStartedAt)
  const groupedTasks = useAppSelector(selectGroupedTasks)

  return {
    groupedTasks,
    roomSessionStartedAt,
  }
}
const MAX_CONSENT_CHECK_TRIES = 25

export interface UseConsentCheckProperties {
  roomId?: string
  sessionId?: string
  skip?: boolean
}

export const useConsentCheck = ({ roomId: roomIdArg, sessionId: sessionIdArg, skip: skipArg }: UseConsentCheckProperties = {}) => {
  const skip = skipArg ?? false
  const { roomId = roomIdArg || '', sessionId = sessionIdArg || '' } = useParams()
  const consentResultAvailable = useRef(false)
  const [consentCheckCounter, setConsentCheckCounter] = useState(0)
  const [consentCheckInterval, setConsentCheckInterval] = useState(3000)
  const [consentTrackingFeatureExecuted, setConsentTrackingFeatureExecuted] = useState(true)

  const { account, accountId, isLoading: accountIsFetching } = useAccount({ refetchOnMountOrArgChange: true })
  const { data: aiFeatureExecutedEvents } = useListRoomEventsQuery(
    {
      accountId,
      roomId,
      sessionId,
      pageRequest: { page: 0, size: 500 },
      types: ['aiFeatureExecutionStarted'],
    },
    { refetchOnMountOrArgChange: true, skip },
  )

  useEffect(() => {
    if (aiFeatureExecutedEvents && aiFeatureExecutedEvents.data) {
      const consentTrackingExecuted = aiFeatureExecutedEvents.data
        .filter((event) => event.type === 'aiFeatureExecutionStarted')
        .flatMap((event) => event.metadata?.executedAiFeatures ?? [])
        .some((feature: AIFeatureInfo) => feature.presetId === CONSENT_TRACKING_PRESET_ID)
      setConsentTrackingFeatureExecuted(consentTrackingExecuted)
    }
  }, [aiFeatureExecutedEvents])

  const { data: roomSession, isLoading: roomIsFetching } = useGetRoomSessionQuery(
    {
      accountId,
      roomId,
      sessionId,
    },
    { refetchOnMountOrArgChange: true, skip },
  )

  const { data: manualParticipantsResults, isLoading: manualParticipantsAreLoading } = useGetManualParticipantsQuery(
    { accountId, roomId, sessionId },
    { refetchOnMountOrArgChange: true, skip },
  )

  const { data: consentResults, isFetching: consentResultsIsFetching } = useGetAiResultsQuery(
    {
      accountId,
      sessionId: sessionId,
      roomId: roomId,
      presetId: CONSENT_TRACKING_PRESET_ID,
      pageRequest: { page: 0, size: 500 },
    },
    {
      refetchOnMountOrArgChange: true,
      skip: skip || consentResultAvailable.current || consentCheckCounter === MAX_CONSENT_CHECK_TRIES || !roomSession,
      pollingInterval: consentCheckInterval,
    },
  )

  useEffect(() => {
    if (!consentResultsIsFetching && consentResults && consentResults.data) {
      consentResultAvailable.current = !!consentResults?.data.length || !consentTrackingFeatureExecuted
    }
    setConsentCheckCounter((consentCheckCounter) => consentCheckCounter + 1)
  }, [consentResultsIsFetching, consentResults, consentTrackingFeatureExecuted])

  useEffect(() => {
    if (consentCheckCounter === MAX_CONSENT_CHECK_TRIES) {
      setConsentCheckInterval(0)
    }
  }, [consentCheckCounter])

  const usersConsent = useMemo(() => {
    if (skip || !roomSession || consentResultsIsFetching || !consentResults || !consentResults.data) {
      return null
    }

    const { users, id: sessionId, roomId } = roomSession.data

    return users
      .filter((user) => !user.id.startsWith('speaker_'))
      .reduce((acc: { [key: string]: UserConsent | null }, user: User) => {
        const consent = consentResults.data.find((r) => r.userId === user.id)
        const userConsent = getUserMetadataConsent(user)
        acc[user.id] = userConsent
          ? userConsent
          : consent
            ? mapToUserConsent((consent.result as StructuredSummaryResult).result as ConsentResult, user, roomId, sessionId)
            : null
        return acc
      }, {})
  }, [roomSession, consentResultsIsFetching, consentResults, skip])

  const meetingDisabledByConsent = useMemo(() => {
    if (!account || !roomSession?.data || !manualParticipantsResults) {
      return false
    }
    if (!account.effectiveConfig.complianceConfig.consentApprovalCheck) {
      return false
    }
    const clientUsers = roomSession.data.users.filter((user) => {
      if (user.id.startsWith('speaker_')) {
        return false
      }
      const roleName = !user.roles.length ? DEFAULT_ROLE : (ASSIGNMENT_ROLE_NAME_MAPPING[user.roles[0].id] ?? user.roles[0].name)
      return roleName === 'Clients'
    })
    const manualParticipantsByUserId = (manualParticipantsResults?.data ?? []).reduce<Record<string, ManualParticipant>>((acc, participant) => {
      acc[participant.userId] = participant
      return acc
    }, {})

    const everyConsentAccepted = clientUsers
      .filter((client) => didManualParticipantAttend(manualParticipantsByUserId[client.id]?.addedReason))
      .map((client) => {
        return usersConsent ? usersConsent[client.id] : null
      })
      .map((usersConsent) => usersConsent?.current?.state ?? UserConsentState.UNKNOWN)
      .every((consentState) => consentState === UserConsentState.ACCEPTED)
    return !everyConsentAccepted
  }, [account, roomSession, usersConsent, manualParticipantsResults])

  const meetingUnlocked = useMemo(() => {
    if (!roomSession?.data) {
      return false
    }
    const roomSessionMetadata = parseMetadata(roomSession.data.metadata)
    return roomSessionMetadata?.external?.meetingUnlocked === true
  }, [roomSession])

  return {
    meetingDisabledByConsent,
    usersConsent,
    consentLoading: roomIsFetching || accountIsFetching || manualParticipantsAreLoading || !consentResults?.data,
    meetingUnlocked: meetingUnlocked,
  }
}
